import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import {
  ILoginRequest,
  ILoginResponse,
  login as loginApi,
} from '../services/login.service';
import { AUTH_KEY } from '../axios/config';
import { getMe } from '../services/login.service';
import { userInfoAtom } from '../utils/atoms';
import { AuthContext } from '../useAuth';
import { PATH } from '@/v1/utils/constants';

/**
 * Provider for authentication management
 * Shares auth state across all components
 */
export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const setUserInfo = useSetAtom(userInfoAtom);
  const location = useLocation();

  const [auth, setAuth] = useState<ILoginResponse | null>(() => {
    try {
      const authData = localStorage.getItem(AUTH_KEY);
      return authData && authData !== 'undefined' ? JSON.parse(authData) : null;
    } catch (error) {
      console.error('Failed to parse authData:', error);
      return null;
    }
  });

  // True after getMe() has completed (reload/login); false until then so PermissionGuard does not redirect before userInfo is ready
  const [isUserInfoLoaded, setIsUserInfoLoaded] = useState(
    () => !auth?.access_token,
  );

  const login = useCallback(async (data: ILoginRequest) => {
    try {
      const loginResponse = await loginApi(data);
      localStorage.setItem(AUTH_KEY, JSON.stringify(loginResponse));
      setAuth(loginResponse);
    } catch (error) {
      console.error('Login failed:', error);
      localStorage.removeItem(AUTH_KEY);
      setAuth(null);
      throw error;
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.clear();
    setAuth(null);
    setUserInfo(null);
  }, [setUserInfo]);

  const isLoggedIn = useMemo(() => !!auth?.access_token, [auth?.access_token]);

  useEffect(() => {
    if (auth?.access_token) {
      setIsUserInfoLoaded(false);
      const fetchCurrentPlan = async () => {
        try {
          const userInfo = await getMe();
          setUserInfo(userInfo);
        } catch (error) {
          console.error('Failed to fetch current plan on refresh:', error);
        } finally {
          setIsUserInfoLoaded(true);
        }
      };
      fetchCurrentPlan();
    } else {
      setIsUserInfoLoaded(true);
    }
  }, [auth?.access_token, setUserInfo]);

  // Refresh user info (getMe) on every route change and sync to userInfoAtom
  useEffect(() => {
    if (!auth?.access_token) return;
    if (location.pathname === PATH.THANKYOU) return;
    const refreshUserInfo = async () => {
      try {
        const userInfo = await getMe();
        setUserInfo(userInfo);
      } catch (error) {
        console.error('Failed to refresh user info on route change:', error);
      }
    };
    refreshUserInfo();
  }, [location.pathname, auth?.access_token, setUserInfo]);

  const updateAuth = useCallback((newAuth: ILoginResponse | null) => {
    if (newAuth) {
      localStorage.setItem(AUTH_KEY, JSON.stringify(newAuth));
      setAuth(newAuth);
    } else {
      localStorage.removeItem(AUTH_KEY);
      setAuth(null);
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      auth,
      updateAuth,
      isUserInfoLoaded,
      login,
      logout,
      isLoggedIn,
      setAuth: updateAuth,
    }),
    [auth, updateAuth, isUserInfoLoaded, login, logout, isLoggedIn],
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};
